import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Heart, ShoppingCart, Minus, Plus, Star, Truck, RefreshCw, Shield } from 'lucide-react';
import Header from './components/Header20';
import Footer from './components/Footer20';
import ProductCard from './components/ProductCard';
import Button from './components/Button';

import { devLog } from '../../../utils/devLog';
const ProductDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [selectedSize, setSelectedSize] = useState('M');
    const [quantity, setQuantity] = useState(1);
    const [selectedImage, setSelectedImage] = useState(0);
    const [isWishlisted, setIsWishlisted] = useState(false);

    // Sample product data - in real app, fetch based on id from API
    const product = {
        id: id || 1,
        name: 'Abstract Expression Tee',
        price: 1299,
        originalPrice: 1799,
        rating: 4.8,
        reviews: 127,
        category: 'Abstract Art',
        description: 'A stunning abstract art piece printed on premium cotton. This unique design captures the essence of creative freedom and artistic expression. Perfect for those who appreciate bold, contemporary fashion.',
        images: [
            'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=800&h=1000&fit=crop',
            'https://images.unsplash.com/photo-1583743814966-8936f5b7be1a?w=800&h=1000&fit=crop',
            'https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=800&h=1000&fit=crop',
            'https://images.unsplash.com/photo-1622445275576-721325763afe?w=800&h=1000&fit=crop',
        ],
        sizes: ['XS', 'S', 'M', 'L', 'XL', 'XXL'],
        colors: ['Black', 'White', 'Navy'],
        fabric: '100% Premium Cotton',
        care: 'Machine wash cold, tumble dry low',
        features: [
            'Premium cotton fabric',
            'Vibrant fade-resistant print',
            'Comfortable regular fit',
            'Designed by independent artists',
            'Sustainable production',
        ],
        inStock: true,
    };

    // Related products
    const relatedProducts = [
        { id: 2, name: 'Urban Nights Hoodie', price: 2499, category: 'urban', tag: 'Popular', image: 'https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=400&h=500&fit=crop' },
        { id: 3, name: 'Nature Print Sweatshirt', price: 1899, category: 'nature', image: 'https://images.unsplash.com/photo-1578587018452-892bacefd3f2?w=400&h=500&fit=crop' },
        { id: 4, name: 'Geometric Pattern Jacket', price: 3499, category: 'abstract', tag: 'Limited', image: 'https://images.unsplash.com/photo-1551028719-00167b16eac5?w=400&h=500&fit=crop' },
        { id: 5, name: 'City Lights Tee', price: 1199, category: 'urban', image: 'https://images.unsplash.com/photo-1583743814966-8936f5b7be1a?w=400&h=500&fit=crop' },
    ];

    const handleQuantityChange = (change) => {
        const newQuantity = quantity + change;
        if (newQuantity >= 1 && newQuantity <= 10) {
            setQuantity(newQuantity);
        }
    };

    const handleAddToCart = () => {
        // Add to cart logic
        devLog.log('Added to cart:', { product, size: selectedSize, quantity });
        // Show success message or redirect to cart
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
            <Header showBackButton={true} cartCount={3} />

            <div className="container mx-auto px-4 lg:px-8 py-8 lg:py-12">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 mb-16">
                    {/* Product Images */}
                    <div className="space-y-4">
                        {/* Main Image */}
                        <div className="relative overflow-hidden rounded-3xl bg-white shadow-2xl aspect-[3/4]">
                            <img
                                src={product.images[selectedImage]}
                                alt={product.name}
                                className="w-full h-full object-cover"
                            />
                            {!product.inStock && (
                                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                    <span className="bg-white text-gray-900 px-6 py-3 rounded-full font-semibold">
                                        Out of Stock
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Thumbnail Images */}
                        <div className="grid grid-cols-4 gap-3">
                            {product.images.map((image, index) => (
                                <button
                                    key={index}
                                    onClick={() => setSelectedImage(index)}
                                    className={`relative overflow-hidden rounded-xl aspect-square transition-all ${
                                        selectedImage === index
                                            ? 'ring-4 ring-orange-500 scale-95'
                                            : 'hover:scale-95 opacity-70 hover:opacity-100'
                                    }`}
                                >
                                    <img
                                        src={image}
                                        alt={`${product.name} ${index + 1}`}
                                        className="w-full h-full object-cover"
                                    />
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Product Info */}
                    <div className="space-y-6">
                        {/* Category Badge */}
                        <div className="inline-block">
                            <span className="px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-full text-sm font-semibold">
                                {product.category}
                            </span>
                        </div>

                        {/* Product Name */}
                        <h1 className="text-3xl lg:text-5xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                            {product.name}
                        </h1>

                        {/* Rating */}
                        <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1">
                                {[...Array(5)].map((_, i) => (
                                    <Star
                                        key={i}
                                        size={20}
                                        className={i < Math.floor(product.rating) ? 'fill-orange-500 text-orange-500' : 'text-gray-300'}
                                    />
                                ))}
                            </div>
                            <span className="text-gray-600">
                                {product.rating} ({product.reviews} reviews)
                            </span>
                        </div>

                        {/* Price */}
                        <div className="flex items-center gap-4">
                            <span className="text-4xl font-bold text-gray-900">₹{product.price}</span>
                            {product.originalPrice && (
                                <>
                                    <span className="text-2xl text-gray-400 line-through">₹{product.originalPrice}</span>
                                    <span className="px-3 py-1 bg-green-500 text-white rounded-full text-sm font-semibold">
                                        {Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)}% OFF
                                    </span>
                                </>
                            )}
                        </div>

                        {/* Description */}
                        <p className="text-gray-700 leading-relaxed text-lg">
                            {product.description}
                        </p>

                        {/* Size Selector */}
                        <div>
                            <h3 className="text-lg font-semibold mb-3 text-gray-900">Select Size</h3>
                            <div className="flex flex-wrap gap-3">
                                {product.sizes.map(size => (
                                    <button
                                        key={size}
                                        onClick={() => setSelectedSize(size)}
                                        className={`px-6 py-3 rounded-full font-medium transition-all ${
                                            selectedSize === size
                                                ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg scale-105'
                                                : 'bg-white text-gray-700 hover:bg-orange-50 border border-orange-200'
                                        }`}
                                    >
                                        {size}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Quantity Selector */}
                        <div>
                            <h3 className="text-lg font-semibold mb-3 text-gray-900">Quantity</h3>
                            <div className="flex items-center gap-4">
                                <button
                                    onClick={() => handleQuantityChange(-1)}
                                    disabled={quantity <= 1}
                                    className="p-3 bg-white rounded-full border border-orange-200 hover:bg-orange-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                >
                                    <Minus size={20} className="text-orange-600" />
                                </button>
                                <span className="text-2xl font-semibold w-12 text-center">{quantity}</span>
                                <button
                                    onClick={() => handleQuantityChange(1)}
                                    disabled={quantity >= 10}
                                    className="p-3 bg-white rounded-full border border-orange-200 hover:bg-orange-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                >
                                    <Plus size={20} className="text-orange-600" />
                                </button>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-4 pt-4">
                            <Button
                                variant="primary"
                                size="lg"
                                fullWidth={true}
                                onClick={handleAddToCart}
                                disabled={!product.inStock}
                            >
                                <ShoppingCart size={20} className="inline mr-2" />
                                Add to Cart
                            </Button>
                            <button
                                onClick={() => setIsWishlisted(!isWishlisted)}
                                className={`p-4 rounded-full border-2 transition-all ${
                                    isWishlisted
                                        ? 'bg-orange-500 border-orange-500'
                                        : 'bg-white border-orange-200 hover:border-orange-500'
                                }`}
                            >
                                <Heart
                                    size={24}
                                    className={isWishlisted ? 'text-white fill-white' : 'text-orange-600'}
                                />
                            </button>
                        </div>

                        {/* Features */}
                        <div className="bg-white rounded-2xl p-6 space-y-4">
                            <div className="flex items-start gap-3">
                                <Truck className="text-orange-500 flex-shrink-0 mt-1" size={24} />
                                <div>
                                    <h4 className="font-semibold text-gray-900">Free Delivery</h4>
                                    <p className="text-gray-600 text-sm">On orders above ₹999</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <RefreshCw className="text-orange-500 flex-shrink-0 mt-1" size={24} />
                                <div>
                                    <h4 className="font-semibold text-gray-900">Easy Returns</h4>
                                    <p className="text-gray-600 text-sm">7-day return policy</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <Shield className="text-orange-500 flex-shrink-0 mt-1" size={24} />
                                <div>
                                    <h4 className="font-semibold text-gray-900">Secure Payment</h4>
                                    <p className="text-gray-600 text-sm">100% secure transactions</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Product Details Tabs */}
                <div className="bg-white rounded-3xl p-6 lg:p-10 mb-16 shadow-lg">
                    <h2 className="text-2xl font-bold mb-6 bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                        Product Details
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div>
                            <h3 className="font-semibold text-lg mb-3 text-gray-900">Key Features</h3>
                            <ul className="space-y-2">
                                {product.features.map((feature, index) => (
                                    <li key={index} className="flex items-start gap-2 text-gray-700">
                                        <span className="text-orange-500 mt-1">•</span>
                                        {feature}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div>
                            <h3 className="font-semibold text-lg mb-3 text-gray-900">Specifications</h3>
                            <div className="space-y-2 text-gray-700">
                                <p><span className="font-medium">Fabric:</span> {product.fabric}</p>
                                <p><span className="font-medium">Care:</span> {product.care}</p>
                                <p><span className="font-medium">Available Colors:</span> {product.colors.join(', ')}</p>
                                <p><span className="font-medium">Available Sizes:</span> {product.sizes.join(', ')}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Related Products */}
                <div>
                    <h2 className="text-3xl font-bold mb-8 text-center bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                        You Might Also Like
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
                        {relatedProducts.map(relatedProduct => (
                            <ProductCard key={relatedProduct.id} product={relatedProduct} />
                        ))}
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default ProductDetail;
